/**
 * Store Thunk Utilities for Result Pattern
 * Helpers for handling Results in thunks with proper error handling
 */

import type { Result } from '../../types/result.interface';
import type { AppError, AuthError } from '../../types/errors.interface';
import { isAuthError } from '../../types/errors.interface';
import { getErrorMessage } from '../../utils/errorMessages';

/**
 * Unwraps a Result and returns the data or throws an error
 * Use when you want to handle errors with try-catch in thunks
 *
 * @param result - Result to unwrap
 * @returns Data if successful
 * @throws AppError if failed
 */
export function unwrapResult<T>(result: Result<T, AppError>): T {
  if (result.success) {
    return result.data;
  }
  throw result.error;
}

/**
 * Checks if Result is successful and returns data with type safety
 *
 * @param result - Result to check
 * @returns Data if successful, null otherwise
 */
export function getResultData<T>(result: Result<T, AppError>): T | null {
  return result.success ? result.data : null;
}

/**
 * Gets user-friendly error message from Result
 *
 * @param result - Failed result
 * @returns User-friendly error message
 */
export function getResultErrorMessage<T>(result: Result<T, AppError>): string {
  if (result.success) {
    return '';
  }

  // The backend only names a field when its message is specific to that field, so
  // that is the signal to show its wording instead of the code's generic entry.
  const fieldScoped = Boolean(result.error.field);

  return getErrorMessage(result.error.code, result.error.message, { fieldScoped });
}

/**
 * Checks if a Result error requires user logout
 * True for AUTH_001 (unauthenticated / 401 errors)
 *
 * @param result - Result to check
 * @returns True if user should be logged out
 */
export function shouldLogoutFromResult<T>(
  result: Result<T, AppError>,
): boolean {
  if (result.success) {
    return false;
  }

  if (isAuthError(result.error)) {
    return result.error.shouldLogout === true;
  }

  return false;
}

/**
 * Checks if a Result error should redirect to login
 *
 * @param result - Result to check
 * @returns True if should redirect to login page
 */
export function shouldRedirectToLogin<T>(result: Result<T, AppError>): boolean {
  if (result.success) {
    return false;
  }

  if (isAuthError(result.error)) {
    return result.error.redirectToLogin === true;
  }

  return false;
}

/**
 * Handles auth errors in thunks
 * Logs out user and redirects if necessary
 *
 * @param result - Failed result with auth error
 * @param authService - Auth service instance (optional, for logout)
 * @param navigate - Navigation function (optional, for redirect)
 */
export function handleAuthError<T>(
  result: Result<T, AppError>,
  options?: {
    onLogout?: () => void;
    onRedirect?: (path: string) => void;
  },
): void {
  if (result.success || !isAuthError(result.error)) {
    return;
  }

  const authError = result.error as AuthError;

  if (authError.shouldLogout && options?.onLogout) {
    options.onLogout();
  }

  if (authError.redirectToLogin && options?.onRedirect) {
    options.onRedirect('/login');
  }
}

/**
 * Extracts validation errors by field from Result
 * Useful for displaying form-level errors
 *
 * @param result - Result with potential validation errors
 * @returns Map of field names to error messages
 */
export function extractValidationErrors<T>(
  result: Result<T, AppError>,
): Map<string, string[]> {
  const errors = new Map<string, string[]>();

  if (result.success) {
    return errors;
  }

  if (result.error.category === 'validation' && result.error.fields) {
    result.error.fields.forEach(field => {
      const existing = errors.get(field.field) || [];
      existing.push(getErrorMessage(field.code, field.message, { fieldScoped: true }));
      errors.set(field.field, existing);
    });
  }

  return errors;
}

/**
 * Converts validation errors to a flat array of error messages
 *
 * @param result - Result with potential validation errors
 * @returns Array of error messages
 */
export function getValidationErrorMessages<T>(
  result: Result<T, AppError>,
): string[] {
  if (result.success) {
    return [];
  }

  if (result.error.category === 'validation' && result.error.fields) {
    return result.error.fields.map(field =>
      getErrorMessage(field.code, field.message, { fieldScoped: true }),
    );
  }

  return [getResultErrorMessage(result)];
}

/**
 * Helper to handle common Result patterns in thunks
 * Automatically dispatches success/failure actions based on Result
 *
 * @param result - Result to handle
 * @param handlers - Success and failure handlers
 */
export async function handleResult<T>(
  result: Result<T, AppError>,
  handlers: {
    onSuccess: (data: T) => void | Promise<void>;
    onFailure: (error: AppError) => void | Promise<void>;
    onAuthError?: (error: AuthError) => void | Promise<void>;
  },
): Promise<void> {
  if (result.success) {
    await handlers.onSuccess(result.data);
  } else {
    // Check for auth errors first
    if (isAuthError(result.error) && handlers.onAuthError) {
      await handlers.onAuthError(result.error as AuthError);
    } else {
      await handlers.onFailure(result.error);
    }
  }
}
